import { WBBox, WBButton, WBFlex, WBLinkButton } from '@admiin-com/ds-web';
import { styled } from '@mui/material';
import { TaskUpload } from './TaskUpload';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bill } from '../PayBillCreateForm';
import { DATE_FORMATS, S3Upload } from '@admiin-com/ds-common';
import { transformDocumentAnalysisToBill } from './transformer';
import { DateTime } from 'luxon';
import { useDefaultValueForFromTo } from '../../../features/providers/TaskCreationProvider/TaskCreationProvider';
import { gql, useMutation } from '@apollo/client';
import {
  createDocumentAnalysis as CREATE_DOCUMENT_ANALYSIS,
  deleteDocumentAnalysis as DELETE_DOCUMENT_ANALYSIS,
  DocumentAnalysis,
  DocumentAnalysisStatus,
  ModelSortDirection,
  TaskCategory,
} from '@admiin-com/ds-graphql';
import { useDefaultFrom } from '../hooks/useDefaultFrom';
import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';

interface Props {
  onNext: () => void;
  onEnter: () => void;
  setBills: (bills: Bill[]) => void;
  bills: Bill[];
  hasMoreData: boolean;
  loadingScanned: boolean;
  loadMoreScanned: () => void;
  appendBill: (bill: Bill) => void;
  removeBill: (key: string) => void;
}

export const UploadBills: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const entity = useDefaultValueForFromTo();
  const entityId = useEntityId();

  const defaultFrom = useDefaultFrom();

  const createBillFromDocument = (document: S3Upload) => {
    return {
      document,
      from: defaultFrom,
      to: entity,
      amount: 0,
      dueAt: DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
      issuedAt: DateTime.now().toFormat(DATE_FORMATS.BACKEND_DATE),
      lineItems: [],
      reference: '',
      checked: true,
      documentSize: document.size,
      documentName: document.name ?? undefined,
    };
  };

  const handleUploaded = () => {
    props.onNext();
  };
  const [ocrTriggeredKeys, setOcrTriggeredKeys] = React.useState<string[]>([]);
  const [createDocumentAnalysis] = useMutation(gql(CREATE_DOCUMENT_ANALYSIS));
  const [deleteDocumentAnalysis] = useMutation(gql(DELETE_DOCUMENT_ANALYSIS));
  const [uploading, setUploading] = React.useState(false);
  return (
    <Container>
      <UploadContainer>
        <TaskUpload
          includeNameInKey
          onRemove={(document) => {
            props.removeBill(document.key);
            deleteDocumentAnalysis({
              variables: {
                input: {
                  id: document.key,
                },
              },
              update: (cache, { data }) => {
                // Evict the deleted DocumentAnalysis entity from the cache
                // This automatically updates ALL queries that reference this entity
                cache.evict({
                  id: cache.identify({
                    __typename: 'DocumentAnalysis',
                    id: document.key,
                  }),
                });

                // Garbage collect to clean up any orphaned references
                cache.gc();
              },
            });
          }}
          bills={props.bills}
          setBills={props.setBills}
          onChange={(documents) => {
            setOcrTriggeredKeys(documents.map((document) => document.key));
            for (const document of documents) {
              props.appendBill(createBillFromDocument(document));
            }
            for (const document of documents) {
              if (ocrTriggeredKeys.includes(document.key)) {
                continue;
              }
              createDocumentAnalysis({
                variables: {
                  input: {
                    entityId: entityId,
                    fileKey: document.key,
                    category: TaskCategory.OTHER,
                  },
                },
              });
            }
          }}
          onAdd={(documentAnalysises) => {
            for (const documentAnalysis of documentAnalysises) {
              props.appendBill({
                ...transformDocumentAnalysisToBill(
                  documentAnalysis,
                  defaultFrom
                ),
                to: entity,
              });
            }
          }}
          loading={props.loadingScanned}
          setLoading={setUploading}
        />
      </UploadContainer>
      <ButtonContainer>
        {props.hasMoreData ? (
          <WBLinkButton
            sx={{
              mt: 1,
              mr: 1,
              px: 3,
              py: 1,
              textDecoration: 'underline',
              color: 'primary.main',
            }}
            onClick={() => {
              props.loadMoreScanned();
            }}
            color="primary"
          >
            {t('loadMore', { defaultValue: 'Load more' })}
          </WBLinkButton>
        ) : (
          <div />
        )}
        <WBFlex sx={{ flexDirection: ['column-reverse', 'row'] }}>
          <WBButton
            variant="outlined"
            onClick={props.onEnter}
            sx={{ mt: 1, mr: 1, px: 3, py: 1 }}
            type="button"
          >
            {t('enterManually', { ns: 'taskbox' })}
          </WBButton>
          <WBButton
            onClick={handleUploaded}
            sx={{ mt: 1, mr: 1, px: 3, py: 1 }}
            disabled={props.bills.length === 0 || uploading}
            type="button"
          >
            {t('next', { ns: 'taskbox' })}
          </WBButton>
        </WBFlex>
      </ButtonContainer>
    </Container>
  );
};
const ButtonContainer = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));
const Container = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  alignItems: 'end',
  padding: theme.spacing(0),
  margin: theme.spacing(0),
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
    width: '100%',
    innerWidth: '100%',
  },
}));

const UploadContainer = styled(WBBox)(({ theme }) => ({
  background: theme.palette.background.default,
  flex: 1,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0),
    width: '100%',
    innerWidth: '100%',
  },
}));
